
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2, RefreshCw, Check, Play, Settings, Type, Palette, Clock, Code, Upload, Image as ImageIcon, Box } from 'lucide-react';
import { preloaderApi, assetUrl } from '../../../services/api';
import { Preloader } from '../../Preloader';

interface PreloaderSettingsData {
  id?: number;
  is_enabled: boolean;
  preloader_type: 'spinner' | 'progress' | 'custom';
  bg_color: string;
  loader_color: string;
  gradient_start?: string;
  gradient_end?: string;
  animation_speed: number;
  display_text?: string;
  show_percentage: boolean;
  min_display_time: number;
  custom_css?: string;
  // Advanced fields
  custom_svg_url?: string;
  custom_svg_size?: string;
  custom_heading?: string;
  reveal_animation?: string;
  emprise_text?: string;
  emprise_font_size?: string;
  box_color?: string;
  text_color?: string;
  preloader_bg_color?: string;
  animation_duration?: number;
}

const PreloaderSettings = () => {
  const [settings, setSettings] = useState<PreloaderSettingsData>({
    is_enabled: true,
    preloader_type: 'spinner',
    bg_color: '#030014',
    loader_color: '#FFBF00',
    animation_speed: 1000,
    display_text: 'Loading...',
    show_percentage: false,
    min_display_time: 1000,
    custom_css: '',
    reveal_animation: 'box-expand',
    emprise_text: 'EMPRISE',
    emprise_font_size: '4rem',
    box_color: '#3b82f6',
    text_color: '#ffffff',
    preloader_bg_color: '#000000',
    animation_duration: 1500
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'appearance' | 'timing' | 'css'>('general');
  const [previewActive, setPreviewActive] = useState(false); // Always disabled to prevent old preloader from showing
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await preloaderApi.getSettings();
      if (res?.success && res.data) {
        setSettings(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch preloader settings', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key: keyof PreloaderSettingsData, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await preloaderApi.updateSettings(settings);
    } catch (error) {
      console.error('Failed to save settings', error);
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploading(true);
      try {
        const file = e.target.files[0];
        const res = await preloaderApi.uploadSvg(file);
        if (res.success && res.url) {
          handleChange('custom_svg_url', res.url);
        }
      } catch (error) {
        console.error('Failed to upload SVG', error);
      } finally {
        setUploading(false);
      }
    }
  };

  const togglePreview = () => {
    setPreviewActive(!previewActive);
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-400">Loading settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Loader2 className="w-6 h-6 text-orange-500" />
          Preloader Settings
        </h2>
        <div className="flex gap-2">
          {/* Preview button removed - new preloader is always active */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="flex gap-2 p-1 bg-white/5 rounded-lg w-fit">
            {[
              { id: 'general', icon: Settings, label: 'General' },
              { id: 'appearance', icon: Palette, label: 'Appearance' },
              { id: 'timing', icon: Clock, label: 'Timing' },
              { id: 'css', icon: Code, label: 'Custom CSS' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm transition-colors ${
                  activeTab === tab.id ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <h3 className="font-medium text-white">Enable Preloader</h3>
                    <p className="text-sm text-gray-400">Show loading screen on initial page load</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.is_enabled}
                      onChange={(e) => handleChange('is_enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Preloader Type</label>
                  <div className="grid grid-cols-3 gap-4">
                    {['spinner', 'progress', 'custom'].map(type => (
                      <button
                        key={type}
                        onClick={() => handleChange('preloader_type', type)}
                        className={`p-4 rounded-lg border text-center capitalize transition-all ${
                          settings.preloader_type === type
                            ? 'border-orange-500 bg-orange-500/10 text-orange-500'
                            : 'border-white/10 bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        <Type className="w-6 h-6 mx-auto mb-2" />
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {settings.preloader_type === 'custom' && (
                  <div className="p-4 bg-white/5 rounded-lg space-y-4 border border-white/10">
                    <h4 className="font-medium text-white flex items-center gap-2">
                        <Upload className="w-4 h-4 text-orange-500" />
                        Custom Assets
                    </h4>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Custom SVG Loader</label>
                        <div className="flex items-center gap-4">
                            {settings.custom_svg_url && (
                                <div className="w-12 h-12 bg-black/40 rounded flex items-center justify-center p-2 border border-white/10">
                                    <img src={assetUrl(settings.custom_svg_url)} alt="Custom SVG" className="w-full h-full object-contain" />
                                </div>
                            )}
                            <label className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg cursor-pointer text-sm text-gray-300 transition-colors">
                                <Upload className="w-4 h-4" />
                                {uploading ? 'Uploading...' : 'Upload SVG'}
                                <input type="file" accept=".svg" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                            </label>
                        </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">SVG Size</label>
                      <input
                        type="text"
                        value={settings.custom_svg_size || ''}
                        onChange={(e) => handleChange('custom_svg_size', e.target.value)}
                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500"
                        placeholder="e.g. 100px, 5rem, 50%"
                      />
                      <p className="text-xs text-gray-500 mt-1">Controls the width/height of the SVG loader.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Custom Heading</label>
                      <input
                        type="text"
                        value={settings.custom_heading || ''}
                        onChange={(e) => handleChange('custom_heading', e.target.value)}
                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500"
                        placeholder="e.g. Welcome"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <h3 className="font-medium text-white border-b border-white/10 pb-2">Preloader Colors</h3>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Background Color</label>
                            <div className="flex gap-2">
                                <input
                                    type="color"
                                    value={settings.preloader_bg_color || settings.bg_color || '#000000'}
                                    onChange={(e) => handleChange('preloader_bg_color', e.target.value)}
                                    className="h-10 w-10 rounded cursor-pointer bg-transparent border-none"
                                />
                                <input
                                    type="text"
                                    value={settings.preloader_bg_color || settings.bg_color || '#000000'}
                                    onChange={(e) => handleChange('preloader_bg_color', e.target.value)}
                                    className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Text/Loader Color</label>
                            <div className="flex gap-2">
                                <input
                                    type="color"
                                    value={settings.text_color || settings.loader_color || '#ffffff'}
                                    onChange={(e) => handleChange('text_color', e.target.value)}
                                    className="h-10 w-10 rounded cursor-pointer bg-transparent border-none"
                                />
                                <input
                                    type="text"
                                    value={settings.text_color || settings.loader_color || '#ffffff'}
                                    onChange={(e) => handleChange('text_color', e.target.value)}
                                    className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="font-medium text-white border-b border-white/10 pb-2">Reveal Animation</h3>
                         <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Box Color</label>
                            <div className="flex gap-2">
                                <input
                                    type="color"
                                    value={settings.box_color || '#3b82f6'}
                                    onChange={(e) => handleChange('box_color', e.target.value)}
                                    className="h-10 w-10 rounded cursor-pointer bg-transparent border-none"
                                />
                                <input
                                    type="text"
                                    value={settings.box_color || '#3b82f6'}
                                    onChange={(e) => handleChange('box_color', e.target.value)}
                                    className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-white/10">
                    <h3 className="font-medium text-white flex items-center gap-2">
                        <Box className="w-4 h-4 text-orange-500" />
                        Emprise Text Settings
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Reveal Text</label>
                            <input
                                type="text"
                                value={settings.emprise_text || 'EMPRISE'}
                                onChange={(e) => handleChange('emprise_text', e.target.value)}
                                className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Font Size</label>
                            <input
                                type="text"
                                value={settings.emprise_font_size || '4rem'}
                                onChange={(e) => handleChange('emprise_font_size', e.target.value)}
                                className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500"
                                placeholder="e.g. 4rem or 64px"
                            />
                        </div>
                    </div>
                </div>
              </div>
            )}

            {activeTab === 'timing' && (
              <div className="space-y-6">
                 <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Reveal Animation Style</label>
                    <select
                        value={settings.reveal_animation || 'box-expand'}
                        onChange={(e) => handleChange('reveal_animation', e.target.value)}
                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500"
                    >
                        <option value="box-expand">Box Expand</option>
                        <option value="fade-zoom">Fade & Zoom</option>
                        <option value="curtain">Curtain Reveal</option>
                    </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Animation Duration (ms)
                  </label>
                  <input
                    type="range"
                    min="500"
                    max="5000"
                    step="100"
                    value={settings.animation_duration || 1500}
                    onChange={(e) => handleChange('animation_duration', parseInt(e.target.value))}
                    className="w-full accent-orange-500"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-2">
                    <span>0.5s</span>
                    <span>{((settings.animation_duration || 1500) / 1000).toFixed(1)}s</span>
                    <span>5s</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Min Display Time (ms)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    step="500"
                    value={settings.min_display_time}
                    onChange={(e) => handleChange('min_display_time', parseInt(e.target.value))}
                    className="w-full accent-orange-500"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-2">
                    <span>0s</span>
                    <span>{(settings.min_display_time / 1000).toFixed(1)}s</span>
                    <span>5s</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'css' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Custom CSS</label>
                  <div className="relative">
                    <textarea
                      value={settings.custom_css || ''}
                      onChange={(e) => handleChange('custom_css', e.target.value)}
                      className="w-full bg-black/20 border border-white/10 rounded-lg p-4 text-sm font-mono text-gray-300 focus:outline-none focus:border-orange-500 h-64"
                      placeholder=".custom-loader { ... }"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Add custom CSS classes for your preloader components.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview Panel */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-medium text-white mb-4">Live Preview</h3>
              <div className="aspect-video bg-black rounded-lg overflow-hidden relative border border-white/10 group">
                {/* Simulated Preview */}
                <div 
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ backgroundColor: settings.preloader_bg_color || settings.bg_color }}
                >
                    {settings.preloader_type === 'custom' ? (
                        <div className="text-center">
                            {settings.custom_svg_url && (
                                <img 
                                    src={assetUrl(settings.custom_svg_url)} 
                                    alt="Logo" 
                                    className="mx-auto mb-4 animate-pulse object-contain"
                                    style={{
                                        width: settings.custom_svg_size || '4rem',
                                        height: settings.custom_svg_size || '4rem'
                                    }}
                                />
                            )}
                            {settings.custom_heading && (
                                <h3 className="text-xl font-bold" style={{ color: settings.text_color }}>{settings.custom_heading}</h3>
                            )}
                             {!settings.custom_svg_url && !settings.custom_heading && (
                                <span className="text-gray-500 text-sm">Upload SVG or add Heading</span>
                             )}
                        </div>
                    ) : settings.preloader_type === 'spinner' ? (
                         <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
                              style={{ borderColor: settings.text_color || settings.loader_color, borderTopColor: 'transparent' }}
                         />
                    ) : (
                         <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                             <div className="h-full w-1/2 animate-pulse" style={{ backgroundColor: settings.text_color || settings.loader_color }} />
                         </div>
                    )}
                </div>
                
                {/* Overlay for Box Reveal Preview */}
                 {settings.reveal_animation === 'box-expand' && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/80">
                         <div className="text-center">
                             <p className="text-white text-xs mb-2">Hover to see Box Reveal</p>
                             <div className="w-20 h-10 flex items-center justify-center border border-white/20 rounded"
                                  style={{ backgroundColor: settings.box_color }}>
                                 <span className="text-[10px] text-white">Content</span>
                             </div>
                         </div>
                    </div>
                 )}
              </div>
              <p className="text-xs text-gray-500 mt-4 text-center">
                This is a simplified preview. Click "Show Preview" in the header to see the full animation.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Full Screen Preview */}
      {previewActive && (
        <Preloader 
            onLoadingComplete={() => setPreviewActive(false)} 
            initialSettings={settings}
        />
      )}
    </div>
  );
};

export default PreloaderSettings;
